import type {
  AdminDevice, GeneratedTag, ReportFilters, ReportResult, SecurityCheckResult,
  TelemetryTag,
  Account, AccountInvitation, ActivationState, AdminCustomer, AdminCustomerDetail, AdminOrderDetail, AdminOrderListItem, AdminPayment,
  AdminAuditLog, AdminBlocklistEntry, AdminCustomerAddress, AdminDeviceModel, AdminLead, AdminPlan, AdminProduct, AdminShipmentListItem, AdminSubscription, AdminSupplier, AdminTelemetryStats, Alert, AlertChannel, AlertKind, AlertRule, ApiError, CatalogOffer, ChannelSwitches, CheckoutBridgeContext, CheckoutOrderResult, CheckoutQuote, ClientVariant, FinancialSummary, FrenetTrackingResult, GeofenceAssignments, HistoryEntry, IntegrationEventItem, IntegrationView, StatementDay, StatementSummary,
  GeofenceSummary, EvidenceExport, InvitationPreview, LiveDeviceEvent, LiveLocation, Member, MembershipRole, MobileConfigurationPayload,
  MobileDeviceSummary, NotificationPreferences, OpenCharge, OrderEmailItem, OrderSummary, SearchItem,
  Paginated, PairingSessionSummary, PendingTerm, ProductGroup, ProductGroupInput, ProductInput, PublicProposal, PublicTerm, PublicTermSummary, SellerClient, SellerInput, SellerMe, SellerProposal, ShareLink, SharedLinkView, SubscriptionSummary, Tag,
  TagDetail, TagGroup, LocationSubject, TermKind, User,
  AdminTerm, AdminTermAcceptance, AdminTermAcceptanceEntry, AdminTermDetail, AdminTermVersion, BillingSummary,
  AdminSeller, CommissionEntry, CommissionOpenPeriod, CommissionPayout,
  AdminBackupConfig, AdminBackupFile, AdminBackupLogItem, AdminBackupRun, AdminBackupStatus,
  AdminBackupCheck, AdminBackupVerify, AdminBackupReport,
} from './types'

export * from './types'
export { resolveApiBaseUrl } from './resolve-api-base-url'

export interface ApiClientOptions {
  /** Ex.: http://localhost:8000 em dev; https://api.loczi.com.br em produção. */
  baseUrl: string
  /** Diagnóstico de apresentação — NUNCA credencial de autorização. */
  variant: ClientVariant
  /** Chamado quando a resposta é 401 — sessão expirada/ausente. */
  onUnauthorized?: () => void
  /** Locale ativo para Accept-Language (ex.: () => i18n.global.locale.value). */
  getLocale?: () => string | undefined
}

/**
 * Cliente da API LOCZI v1.
 * - Autenticação por cookie de sessão Sanctum (credentials: 'include').
 * - CSRF: lê o cookie XSRF-TOKEN e envia X-XSRF-TOKEN.
 * - Conta corrente: header X-Account-Id por requisição (public_id opaco) —
 *   nunca confiar em contexto global; o servidor valida o vínculo.
 */
export function createApi(options: ApiClientOptions) {
  const { baseUrl, variant, onUnauthorized, getLocale } = options
  let currentAccountId: string | null = null
  let csrfReady: Promise<void> | null = null

  function xsrfToken(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
    return match ? decodeURIComponent(match[1]) : null
  }

  /** Garante o cookie CSRF antes da primeira mutação (Sanctum SPA). */
  async function ensureCsrf(): Promise<void> {
    csrfReady ??= fetch(`${baseUrl}/sanctum/csrf-cookie`, {
      credentials: 'include',
    }).then(() => undefined).catch(() => {
      csrfReady = null // permite nova tentativa
    })
    return csrfReady
  }

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    init: { signal?: AbortSignal; query?: Record<string, string | number | undefined>; responseType?: 'blob' } = {},
  ): Promise<T> {
    if (method !== 'GET') await ensureCsrf()

    const qs = init.query
      ? '?' + new URLSearchParams(
          Object.fromEntries(
            Object.entries(init.query).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]),
          ),
        ).toString()
      : ''

    const doFetch = () => {
      const headers: Record<string, string> = {
        Accept: 'application/json',
        'X-Client-Variant': variant,
      }
      const locale = getLocale?.()
      if (locale) headers['Accept-Language'] = locale
      if (body !== undefined) headers['Content-Type'] = 'application/json'
      if (currentAccountId) headers['X-Account-Id'] = currentAccountId
      const xsrf = xsrfToken()
      if (xsrf && method !== 'GET') headers['X-XSRF-TOKEN'] = xsrf

      return fetch(`${baseUrl}/api/v1${path}${qs}`, {
        method,
        headers,
        credentials: 'include',
        signal: init.signal,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      })
    }

    let res = await doFetch()

    // Cookie XSRF dessincronizado da sessão (abas/subdomínios dividem o jar)
    // → renova o par via csrf-cookie e tenta uma vez mais antes de falhar.
    if (res.status === 419 && method !== 'GET') {
      csrfReady = null
      await ensureCsrf()
      res = await doFetch()
    }

    if (res.status === 401) {
      onUnauthorized?.()
      throw { message: 'Sessão expirada.', status: 401 } satisfies ApiError
    }

    if (!res.ok) {
      let payload: { message?: string; errors?: Record<string, string[]>; code?: string; extensions?: Record<string, unknown> } = {}
      try { payload = await res.json() } catch { /* corpo não-JSON */ }
      throw {
        message: payload.message ?? `Erro ${res.status}`,
        errors: payload.errors,
        status: res.status,
        code: payload.code,
        extensions: payload.extensions,
      } satisfies ApiError
    }

    if (init.responseType === 'blob') return res.blob() as Promise<T>
    return res.json() as Promise<T>
  }

  return {
    /** Define a conta corrente deste cliente (por instância, nunca global). */
    setAccount(publicId: string | null) {
      currentAccountId = publicId
    },
    getAccount() {
      return currentAccountId
    },
    ensureCsrf,

    // ---- Auth -----------------------------------------------------------
    auth: {
      register: (input: { name: string; email: string; password: string; password_confirmation: string }) =>
        request<{ data: User }>('POST', '/auth/register', input),
      login: (input: { email: string; password: string }) =>
        request<{ data: User }>('POST', '/auth/login', input),
      forgotPassword: (email: string) =>
        request<{ data: { sent: boolean } }>('POST', '/auth/forgot-password', { email }),
      resetPassword: (input: { email: string; code: string; password: string; password_confirmation: string }) =>
        request<{ data: { reset: boolean } }>('POST', '/auth/reset-password', input),
      logout: () => request<{ data: { logged_out: boolean } }>('POST', '/auth/logout'),
      me: () => request<{ data: User }>('GET', '/me'),
      updateLocale: (input: { locale?: string; timezone?: string }) =>
        request<{ data: { locale: string; timezone: string } }>('PATCH', '/me/locale', input),
      googleRedirect: (returnTo: string) =>
        request<{ data: { url: string } }>('GET', `/auth/google/redirect`, undefined, {
          query: { surface: variant, return_to: returnTo },
        }),
    },

    // ---- Accounts -------------------------------------------------------
    accounts: {
      list: () => request<{ data: Account[] }>('GET', '/accounts'),
      select: (publicId: string) =>
        request<{ data: Account }>('POST', '/accounts/select', { account_id: publicId }),
      current: () => request<{ data: Account }>('GET', '/account'),
      /** Cobrança em aberto da conta — PIX/fatura para o "Pagar agora". */
      openCharge: () => request<{ data: OpenCharge | null }>('GET', '/account/open-charge'),
      financialSummary: () => request<{ data: FinancialSummary }>('GET', '/account/financial-summary'),
    },

    // ---- Tags (devices) --------------------------------------------------
    tags: (() => {
      const list = (filters: {
        search?: string; category?: string; group_id?: number; update?: string
        page?: number; per_page?: number
      }, signal?: AbortSignal) =>
        request<Paginated<Tag>>('GET', '/tags', undefined, {
          signal,
          query: {
            search: filters.search, category: filters.category,
            group_id: filters.group_id, update: filters.update,
            page: filters.page, per_page: filters.per_page,
          },
        })
      return {
        list,
        /** Todas as tags ativas da conta (paginado de 100 em 100; limite da API). */
        listAll: async (filters: {
          search?: string; category?: string; group_id?: number; update?: string
        } = {}, signal?: AbortSignal) => {
          const first = await list({ ...filters, page: 1, per_page: 100 }, signal)
          if (first.meta.last_page <= 1) return first.data
          const rest = await Promise.all(
            Array.from({ length: first.meta.last_page - 1 }, (_, i) =>
              list({ ...filters, page: i + 2, per_page: 100 }, signal)),
          )
          return [...first.data, ...rest.flatMap((p) => p.data)]
        },
        get: (id: string, signal?: AbortSignal) =>
          request<{ data: TagDetail }>('GET', `/tags/${id}`, undefined, { signal }),
        update: (id: string, input: { name?: string; category?: string; group_ids?: number[] }) =>
          request<{ data: Tag }>('PATCH', `/tags/${id}`, input),
      }
    })(),

    // ---- Ativação ---------------------------------------------------------
    activations: {
      create: (input: {
        code: string; secret: string; name: string
        category: string; group_id?: number
      }) => request<{
        data: {
          id: string
          activation_state: ActivationState
          result: 'awaiting_first_detection' | 'location_received'
          message: string
        }
      }>('POST', '/activations', input),
    },

    // ---- Grupos ------------------------------------------------------------
    groups: {
      list: () => request<{ data: Array<TagGroup & { devices_count: number; devices?: Array<{ id: string; name: string | null }> }> }>('GET', '/groups'),
      create: (input: { name: string; color?: string }) =>
        request<{ data: TagGroup }>('POST', '/groups', input),
      update: (id: number, input: { name: string; color?: string | null }) =>
        request<{ data: TagGroup & { devices_count: number } }>('PATCH', `/groups/${id}`, input),
      remove: (id: number) => request<{ data: { deleted: boolean } }>('DELETE', `/groups/${id}`),
      syncDevices: (id: number, deviceIds: string[]) =>
        request<{ data: TagGroup & { devices_count: number } }>('PUT', `/groups/${id}/devices`, { device_ids: deviceIds }),
    },

    // ---- Regras de alerta ----------------------------------------------------
    alertRules: {
      list: () => request<{ data: AlertRule[] }>('GET', '/alert-rules'),
      update: (kind: AlertKind, input: { channels: AlertChannel[]; active: boolean; config?: Record<string, number> }) =>
        request<{ data: AlertRule }>('PUT', `/alert-rules/${kind}`, input),
    },

    // ---- Alertas -----------------------------------------------------------
    alerts: {
      list: (filters: { read?: 'read' | 'unread'; kind?: AlertKind; page?: number; per_page?: number } = {}, signal?: AbortSignal) =>
        request<Paginated<Alert>>('GET', '/alerts', undefined, {
          signal,
          query: { read: filters.read, kind: filters.kind, page: filters.page, per_page: filters.per_page },
        }),
      get: (id: string) => request<{ data: Alert }>('GET', `/alerts/${id}`),
      markRead: (id: string) => request<{ data: { read: boolean } }>('POST', `/alerts/${id}/read`),
      markAllRead: () => request<{ data: { read: number } }>('POST', '/alerts/read-all'),
    },

    // ---- Preferências de notificação ------------------------------------------
    notificationPrefs: {
      get: () => request<{ data: NotificationPreferences }>('GET', '/notification-preferences'),
      update: (input: { kind?: AlertKind; channels: Partial<ChannelSwitches> }) =>
        request<{ data: { kind: AlertKind | null; channels: ChannelSwitches } }>('PUT', '/notification-preferences', input),
      reset: (kind?: AlertKind) =>
        request<{ data: { reset: boolean } }>('DELETE', '/notification-preferences', undefined, { query: { kind } }),
    },

    // ---- Cercas -------------------------------------------------------------
    geofences: {
      list: () => request<{ data: GeofenceSummary[] }>('GET', '/geofences'),
      update: (id: number, input: { name: string; shape: 'circle' | 'polygon'; geometry: Record<string, unknown>; color?: string; is_private?: boolean }) =>
        request<{ data: { id: number } }>('PATCH', `/geofences/${id}`, input),
      create: (input: {
        name: string
        shape: 'circle' | 'polygon'
        geometry: Record<string, unknown>
        color?: string
        notify_enter?: boolean
        notify_exit?: boolean
        /** Zona privada (§19.6) — só shape 'circle'. */
        is_private?: boolean
      }) => request<{ data: { id: number } }>('POST', '/geofences', input),
      setAssignments: (id: number, input: GeofenceAssignments) =>
        request<{ data: GeofenceAssignments }>('PUT', `/geofences/${id}/assignments`, input),
    },

    // ---- Busca global (itens da conta corrente) ------------------------------
    search: (q: string, signal?: AbortSignal) =>
      request<{ data: SearchItem[] }>('GET', '/search', undefined, { signal, query: { q } }),

    // ---- Pedidos e assinatura -------------------------------------------------
    orders: {
      list: (page = 1, focus?: string) =>
        request<Paginated<OrderSummary>>('GET', '/orders', undefined, { query: { page, focus } }),
      get: (id: string) => request<{ data: OrderSummary }>('GET', `/orders/${id}`),
    },
    subscription: {
      current: () => request<{ data: SubscriptionSummary | null }>('GET', '/subscription'),
      /** Cancela no fim do período pago — serviço segue até current_period_ends_at. */
      cancel: (reason?: string) =>
        request<{ data: SubscriptionSummary }>('POST', '/subscription/cancel', { reason }),
      /** Troca de plano: upgrade aplica já; downgrade recusa com PLAN_DOWNGRADE_BLOCKED. */
      changePlan: (planId: number) =>
        request<{ data: SubscriptionSummary }>('POST', '/subscription/change-plan', { plan_id: planId }),
    },

    // ---- Termos de adesão / LGPD / uso (SPEC-TERMOS) --------------------------
    terms: {
      /** Versões vigentes publicadas — usado no checkout e no re-aceite. */
      current: () => request<{ data: PublicTermSummary[] }>('GET', '/terms'),
      /** Texto completo (markdown) da versão vigente de um kind. */
      get: (kind: TermKind) => request<{ data: PublicTerm }>('GET', `/terms/${kind}`),
      /** Registra aceite autenticado (re-aceite) e devolve os pendentes restantes. */
      accept: (terms: { kind: TermKind; version: number }[]) =>
        request<{ data: { pending_terms: PendingTerm[] } }>('POST', '/me/terms/accept', { terms }),
    },

    // ---- Push ---------------------------------------------------------------
    push: {
      subscribe: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
        request<{ data: { id: number } }>('POST', '/push/subscriptions', subscription),
      unsubscribe: (endpoint: string) =>
        request<{ data: { revoked: boolean } }>('DELETE', '/push/subscriptions', { endpoint }),
    },

    // ---- LOCZI Mobile — pessoas e aparelhos Android (painel) -------------------
    mobile: {
      subjects: () => request<{ data: LocationSubject[] }>('GET', '/mobile/subjects'),
      createSubject: (name: string) =>
        request<{ data: LocationSubject }>('POST', '/mobile/subjects', { name }),
      /** Cria sessão de pareamento; o código de 6 dígitos só vem nesta resposta. */
      createPairing: (input: { subject_id?: number; subject_name?: string }) =>
        request<{ data: PairingSessionSummary & { code: string } }>('POST', '/mobile/pairing-sessions', input),
      pairingSession: (id: string, signal?: AbortSignal) =>
        request<{ data: PairingSessionSummary }>('GET', `/mobile/pairing-sessions/${id}`, undefined, { signal }),
      approvePairing: (id: string) =>
        request<{ data: Pick<PairingSessionSummary, 'id' | 'state'> }>('POST', `/mobile/pairing-sessions/${id}/approve`),
      cancelPairing: (id: string) =>
        request<{ data: Pick<PairingSessionSummary, 'id' | 'state'> }>('DELETE', `/mobile/pairing-sessions/${id}`),
      devices: () => request<{ data: MobileDeviceSummary[] }>('GET', '/mobile/devices'),
      revokeDevice: (id: string) =>
        request<{ data: { id: string; revoked: boolean } }>('POST', `/mobile/devices/${id}/revoke`),
      /** Revoga TODOS os aparelhos — pré-requisito p/ reduzir mobile_limit (downgrade). */
      revokeAllDevices: () =>
        request<{ data: { revoked: number } }>('POST', '/mobile/devices/revoke-all'),
      /** PATCH parcial — o backend faz merge sobre a revisão vigente. */
      updateDeviceConfiguration: (id: string, payload: Partial<MobileConfigurationPayload>) =>
        request<{ data: { revision: number; schema_version: number } }>(
          'PATCH', `/mobile/devices/${id}/configuration`, { payload },
        ),
      /** Governança do app: desvínculo pelo aparelho + modo oculto (stealth). */
      updateDeviceControls: (id: string, controls: { allow_app_unlink?: boolean; stealth_enabled?: boolean }) =>
        request<{ data: { allow_app_unlink: boolean; stealth_enabled: boolean; revision: number } }>(
          'PATCH', `/mobile/devices/${id}/controls`, controls,
        ),
      /** "Apitar": marca o comando; o aparelho o recebe no próximo contato. */
      beepDevice: (id: string) =>
        request<{ data: { beep_pending: boolean; beep_requested_at: string } }>(
          'POST', `/mobile/devices/${id}/beep`,
        ),
      /** Evidência forense (§19.5): posições assinadas + cadeia SHA-256. */
      evidence: (id: string, params?: { from?: string; to?: string; limit?: number }) =>
        request<{ data: EvidenceExport }>(
          'GET', `/mobile/devices/${id}/evidence`, undefined, { query: params },
        ),
    },

    // ---- Links públicos de compartilhamento (painel) --------------------------
    shareLinks: {
      list: () => request<{ data: ShareLink[] }>('GET', '/share-links'),
      create: (input: { device_id: string; ttl_minutes: number }) =>
        request<{ data: ShareLink }>('POST', '/share-links', input),
      revoke: (id: string) =>
        request<{ data: { id: string; revoked: boolean } }>('DELETE', `/share-links/${id}`),
    },

    // ---- Membros e convites da conta -------------------------------------
    members: {
      list: () => request<{ data: Member[] }>('GET', '/members'),
      updateRole: (id: number, role: MembershipRole) =>
        request<{ data: { id: number; role: MembershipRole } }>('PATCH', `/members/${id}`, { role }),
      remove: (id: number) =>
        request<{ data: { removed: boolean } }>('DELETE', `/members/${id}`),
    },
    invitations: {
      list: () => request<{ data: AccountInvitation[] }>('GET', '/invitations'),
      create: (input: { email: string; role: MembershipRole }) =>
        request<{ data: AccountInvitation }>('POST', '/invitations', input),
      revoke: (id: number) =>
        request<{ data: AccountInvitation }>('DELETE', `/invitations/${id}`),
      /** Prévia/aceite pelo convidado logado — fora do contexto de conta. */
      preview: (token: string) =>
        request<{ data: InvitationPreview }>('GET', `/invitations/${token}`),
      accept: (token: string) =>
        request<{ data: Account }>('POST', `/invitations/${token}/accept`),
    },

    // ---- Catálogo público (site/checkout) ------------------------------------
    catalog: {
      plans: () => request<{ data: CatalogOffer[] }>('GET', '/catalog/plans'),
    },
    checkout: {
      /** Ponte painel → site: link curto com pedido mínimo + dados da conta. */
      bridge: (input: { module?: string; sku?: string; qty?: number; source?: string }) =>
        request<{ data: { token: string; url: string; expires_at: string } }>('POST', '/checkout/bridge', input),
      /** Abre o contexto do token (público) para o site pré-carregar o checkout. */
      bridgeContext: (token: string) =>
        request<{ data: CheckoutBridgeContext }>('GET', `/checkout/bridge/${encodeURIComponent(token)}`),
      /** Cotação do carrinho: preços, planos obrigatórios e frete recalculados no servidor. */
      quote: (input: {
        items: { sku: string; quantity: number }[]
        shipping_postal_code?: string
      }) =>
        request<{ data: CheckoutQuote }>('POST', '/checkout/quote', input),
      /** Cria pedido real + cobrança no gateway (PIX/boleto/cartão). */
      createOrder: (input: {
        items: { sku: string; quantity: number }[]
        customer: { name: string; email: string; phone?: string; cpf_cnpj: string }
        payment_method: 'pix' | 'boleto' | 'credit_card'
        card?: {
          holder_name: string
          number: string
          expiry_month: string
          expiry_year: string
          ccv: string
        }
        address?: {
          postal_code: string
          street?: string
          number?: string
          complement?: string
          district?: string
          city?: string
          state?: string
        }
        /** Vínculo comercial: código do vendedor (?ref=) ou token da proposta (?p=). */
        seller_code?: string
        proposal_token?: string
        /** Ponte painel → site: amarra o pedido à conta logada. */
        bridge_token?: string
        source?: string
      }) =>
        request<{ data: CheckoutOrderResult }>('POST', '/checkout/orders', input),
      lead: (input: { name?: string; email?: string; phone?: string; quantity?: number; product_sku?: string; note?: string; seller_code?: string }) =>
        request<{ data: { registered: boolean } }>('POST', '/checkout/leads', input),
      /** Compra autenticada dentro do painel (SPEC-BILLING §9) — ex.: +1 licença mobile. */
      buy: (input: {
        items: { sku: string; quantity: number }[]
        payment_method: 'pix' | 'boleto' | 'credit_card'
        customer?: { cpf_cnpj?: string }
        card?: { holder_name: string; number: string; expiry_month: string; expiry_year: string; ccv: string }
        terms?: { kind: TermKind; version: number }[]
        address?: { postal_code?: string }
      }) =>
        request<{ data: CheckoutOrderResult }>('POST', '/checkout/buy', input),
    },

    reports: {
      devices: async (signal?: AbortSignal) => {
        const rows: { id: string; name: string }[] = []
        let page = 1
        let last = 1
        do {
          const response = await request<Paginated<{ id: string; name: string }>>('GET', '/reports/devices', undefined, { signal, query: { page } })
          rows.push(...response.data)
          last = response.meta.last_page
          page++
        } while (page <= last)
        return rows
      },
      list: (filters: ReportFilters, signal?: AbortSignal) =>
        request<ReportResult>('GET', '/reports', undefined, { query: { ...filters }, signal }),
      export: (filters: ReportFilters, signal?: AbortSignal) =>
        request<Blob>('GET', '/reports/export', undefined, { query: { ...filters }, signal, responseType: 'blob' }),
      securityCheck: (filters: Omit<ReportFilters, 'type'>, signal?: AbortSignal) =>
        request<SecurityCheckResult>('GET', '/reports/security-check', undefined, { query: { ...filters }, signal }),
      securityCheckPdf: (filters: Omit<ReportFilters, 'type'>, signal?: AbortSignal) =>
        request<Blob>('GET', '/reports/security-check/pdf', undefined, { query: { ...filters }, signal, responseType: 'blob' }),
    },

    // ---- Ao vivo (SSE) ----------------------------------------------------
    history: (params: { device?: string; from?: string; to?: string; page?: number; per_page?: number } = {}) =>
      request<Paginated<HistoryEntry> & { meta: Paginated<HistoryEntry>['meta'] & { retention_days: number } }>(
        'GET', '/history', undefined, { query: params }),

    live: {
      /**
       * Assina o stream SSE da conta corrente (`GET /stream`). O EventSource
       * reconecta sozinho com Last-Event-ID; retorna a função de cleanup.
       * Auth = cookie Sanctum (withCredentials); conta por query `?account=`
       * porque EventSource não envia headers custom.
       */
      subscribe(handlers: {
        location?: (data: LiveLocation) => void
        device?: (data: LiveDeviceEvent) => void
        open?: () => void
        error?: (event: Event) => void
      }): () => void {
        const qs = currentAccountId ? `?account=${encodeURIComponent(currentAccountId)}` : ''
        const source = new EventSource(`${baseUrl}/api/v1/stream${qs}`, { withCredentials: true })
        source.addEventListener('location', (e) => {
          handlers.location?.(JSON.parse((e as MessageEvent<string>).data))
        })
        source.addEventListener('device', (e) => {
          handlers.device?.(JSON.parse((e as MessageEvent<string>).data))
        })
        source.onopen = () => handlers.open?.()
        source.onerror = (e) => handlers.error?.(e)
        return () => source.close()
      },
    },

    // ---- Admin ---------------------------------------------------------------
    admin: {
      dashboard: () => request<{ data: Record<string, Record<string, number>> }>('GET', '/admin/dashboard'),
      /** Volumetria de telemetria — página dedicada de acompanhamento. */
      telemetryStats: () => request<{ data: AdminTelemetryStats }>('GET', '/admin/telemetry/stats'),
      customers: (search = '', all = false) =>
        request<{ data: AdminCustomer[] }>('GET', '/admin/customers', undefined, { query: { search, all: all ? 1 : undefined } }),
      /** Página dedicada do cliente: perfil + contas com tags/pedidos/assinaturas/pagamentos. */
      customer: (id: number) =>
        request<{ data: AdminCustomerDetail }>('GET', `/admin/customers/${id}`),
      updateCustomer: (id: number, input: { name?: string; email?: string; phone?: string | null; disabled?: boolean; address?: AdminCustomerAddress | null }) =>
        request<{ data: AdminCustomer }>('PUT', `/admin/customers/${id}`, input),
      /** Congela/descongela uma conta do cliente (frozen → status 'suspended'). */
      freezeCustomerAccount: (userId: number, accountPublicId: string, frozen: boolean) =>
        request<{ data: AdminCustomer }>('PUT', `/admin/customers/${userId}/accounts/${accountPublicId}`, { frozen }),
      // Catálogo — fornecedores e produtos.
      suppliers: (q = '') =>
        request<{ data: AdminSupplier[] }>('GET', '/admin/suppliers', undefined, { query: { q } }),
      createSupplier: (input: { name: string; country?: string | null; status?: string; email?: string | null; phone?: string | null; document?: string | null }) =>
        request<{ data: AdminSupplier }>('POST', '/admin/suppliers', input),
      updateSupplier: (id: number, input: { name: string; country?: string | null; status?: string; email?: string | null; phone?: string | null; document?: string | null }) =>
        request<{ data: AdminSupplier }>('PUT', `/admin/suppliers/${id}`, input),
      /** Grid de produtos com filtros server-side. */
      products: (params: {
        q?: string
        product_for?: string
        status?: string
        active?: 'yes' | 'no'
        recurring?: 'yes' | 'no'
        shipping?: 'yes' | 'no'
        group?: string
      } = {}) =>
        request<{ data: AdminProduct[]; meta: { total: number; returned: number; limit: number } }>('GET', '/admin/products', undefined, { query: params }),
      deviceModels: () =>
        request<{ data: AdminDeviceModel[] }>('GET', '/admin/device-models'),
      /** Planos ativos — alimenta o select "plano obrigatório" do produto. */
      plans: () =>
        request<{ data: AdminPlan[] }>('GET', '/admin/plans'),
      createProduct: (input: ProductInput) =>
        request<{ data: AdminProduct }>('POST', '/admin/products', input),
      updateProduct: (id: number, input: ProductInput) =>
        request<{ data: AdminProduct }>('PUT', `/admin/products/${id}`, input),
      deleteProduct: (id: number) =>
        request<{ data: { deleted: boolean } }>('DELETE', `/admin/products/${id}`),
      /** Grupos de seleção do catálogo (exclusividade no checkout). */
      productGroups: () =>
        request<{ data: ProductGroup[] }>('GET', '/admin/product-groups'),
      createProductGroup: (input: ProductGroupInput) =>
        request<{ data: ProductGroup }>('POST', '/admin/product-groups', input),
      updateProductGroup: (id: number, input: ProductGroupInput) =>
        request<{ data: ProductGroup }>('PUT', `/admin/product-groups/${id}`, input),
      deleteProductGroup: (id: number) =>
        request<{ data: { deleted: boolean } }>('DELETE', `/admin/product-groups/${id}`),
      trackFrenet: (input: { tracking_code: string; service_code?: string; order_number?: string }) =>
        request<FrenetTrackingResult>('POST', '/admin/integrations/frenet/track', input),
      sendBrevoTest: (email: string) =>
        request<{ ok: boolean; message: string }>('POST', '/admin/integrations/brevo/send-test', { email }),
      auditLogs: (params: { q?: string; action?: string; from?: string; to?: string; page?: number } = {}) =>
        request<Paginated<AdminAuditLog>>('GET', '/admin/audit-logs', undefined, { query: params }),
      impersonate: (input: { user_id: number; account_id?: string; reason: string }) =>
        request<{ data: { impersonation_id: number; expires_at: string } }>('POST', '/admin/impersonations', input),
      stopImpersonation: () =>
        request<{ data: { ended: boolean } }>('DELETE', '/admin/impersonations/current'),

      // Pedidos, pagamentos e remessas — operação da plataforma.
      orders: (params: { state?: string; tag?: string; shipping?: string; q?: string; page?: number } = {}) =>
        request<Paginated<AdminOrderListItem>>('GET', '/admin/orders', undefined, { query: params }),
      order: (publicId: string) =>
        request<{ order: AdminOrderDetail }>('GET', `/admin/orders/${publicId}`),
      orderEmails: (publicId: string) =>
        request<{ data: OrderEmailItem[] }>('GET', `/admin/orders/${publicId}/emails`),
      transitionOrder: (publicId: string, to: string, reason?: string) =>
        request<{ order: AdminOrderDetail }>('POST', `/admin/orders/${publicId}/transitions`, { to, reason }),
      /** Pedido interno — troca/perda de tag, venda avulsa; aceita internal_only e dispensa plano. */
      createOrder: (input: {
        user_id: number
        account_id: string
        items: { sku: string; quantity: number; unit_price_cents?: number }[]
        reason: 'troca_quebrada' | 'perda' | 'venda_interna' | 'outro'
        include_plan?: boolean
        recurring?: boolean
        needs_shipping?: boolean
        shipping_cents?: number
        shipping_address?: Record<string, string>
        payment?: 'pending' | 'offline_paid'
        note?: string
        seller_id?: number | null
      }) =>
        request<{ order: AdminOrderDetail }>('POST', '/admin/orders', input),
      /** Atribui/troca o vendedor do pedido (null desvincula). */
      assignOrderSeller: (publicId: string, sellerId: number | null) =>
        request<{ order: AdminOrderDetail }>('PUT', `/admin/orders/${publicId}/seller`, { seller_id: sellerId }),
      /** Relaciona as peças geradas ao pedido (sync; não ativa a tag). */
      syncOrderDevices: (publicId: string, deviceIds: string[]) =>
        request<{ order: AdminOrderDetail }>('PUT', `/admin/orders/${publicId}/devices`, { device_ids: deviceIds }),
      payments: (params: { state?: string; page?: number } = {}) =>
        request<Paginated<AdminPayment>>('GET', '/admin/payments', undefined, { query: params }),
      paymentStatement: (params: { from?: string; to?: string } = {}) =>
        request<{ data: StatementDay[]; summary: StatementSummary }>('GET', '/admin/payments/statement', undefined, { query: params }),
      paymentEvents: () =>
        request<{ data: IntegrationEventItem[] }>('GET', '/admin/payments/events'),
      subscriptions: () =>
        request<{ data: AdminSubscription[]; summary: { active: number; pending: number; mrr_cents: number } }>('GET', '/admin/payments/subscriptions'),
      syncPayment: (publicId: string) =>
        request<{ payment: { public_id: string; state: string; paid_at: string | null } }>('POST', `/admin/payments/${publicId}/sync`),
      decideChargeback: (publicId: string, decision: 'accepted' | 'contested') =>
        request<{ payment: { public_id: string; state: string; chargeback_at: string | null; chargeback_decision: string | null; chargeback_decided_at: string | null } }>('POST', `/admin/payments/${publicId}/chargeback`, { decision }),
      sendPaymentMethodLink: (publicId: string) =>
        request<{ sent: boolean; invoice_url: string }>('POST', `/admin/payments/${publicId}/payment-method-link`),
      shipments: (params: { state?: string; q?: string; page?: number } = {}) =>
        request<Paginated<AdminShipmentListItem>>('GET', '/admin/shipments', undefined, { query: params }),
      createShipment: (input: { order_public_id: string; carrier: string; service: string; tracking_code?: string; volumes?: number; cost_cents?: number; expected_delivery_at?: string; device_ids?: string[] }) =>
        request<{ shipment: { public_id: string; state: string; devices?: { id: string; tag_code: string | null }[] } }>('POST', '/admin/shipments', input),
      addShipmentEvent: (publicId: string, input: { code: string; description: string; tracking_code?: string }) =>
        request<{ shipment: { public_id: string; state: string } }>('POST', `/admin/shipments/${publicId}/events`, input),
      integration: (provider: string) =>
        request<IntegrationView>('GET', `/admin/integrations/${provider}`),
      updateIntegration: (provider: string, values: Record<string, string>) =>
        request<IntegrationView>('PUT', `/admin/integrations/${provider}`, values),
      testIntegration: (provider: string) =>
        request<{ ok: boolean; message: string }>('POST', `/admin/integrations/${provider}/test`),
      integrationEvents: (provider: string, status?: string) =>
        request<{ data: IntegrationEventItem[] }>('GET', `/admin/integrations/${provider}/events`, undefined, { query: { status } }),
      retryIntegrationEvent: (provider: string, id: number) =>
        request<{ event: IntegrationEventItem }>('POST', `/admin/integrations/${provider}/events/${id}/retry`),

      // ---- Backup do banco → Bunny Storage (CDN) ---------------------------
      backupStatus: () =>
        request<{ data: AdminBackupStatus }>('GET', '/admin/backup'),
      updateBackupConfig: (input: { host: string; storage_zone: string; base_path?: string; api_key?: string; is_active?: boolean }) =>
        request<{ data: { config: AdminBackupConfig } }>('PUT', '/admin/backup/config', input),
      validateBackupConnection: () =>
        request<{ data: { ok: boolean; message: string } }>('POST', '/admin/backup/validate'),
      runBackup: (ambiente = 'production') =>
        request<{ data: AdminBackupRun }>('POST', '/admin/backup/run', { ambiente }),
      backupFiles: () =>
        request<{ data: { local: AdminBackupFile[]; remote: AdminBackupFile[] } }>('GET', '/admin/backup/files'),
      downloadBackup: (filename: string, source: 'local' | 'remote') =>
        request<Blob>('GET', '/admin/backup/download', undefined, { query: { filename, source }, responseType: 'blob' }),
      deleteBackupFile: (filename: string, source: 'local' | 'remote') =>
        request<{ data: { removed: boolean } }>('DELETE', '/admin/backup/file', { filename, source }),
      clearBackupLogs: () =>
        request<{ data: { removed: number } }>('DELETE', '/admin/backup/logs'),
      backupRequirements: () =>
        request<{ data: { checks: AdminBackupCheck[]; all_ok: boolean } }>('GET', '/admin/backup/requirements'),
      verifyBackup: () =>
        request<{ data: AdminBackupVerify }>('POST', '/admin/backup/verify'),
      sendBackupReport: (days = 7) =>
        request<{ data: AdminBackupReport }>('POST', '/admin/backup/report', { days }),
      /** Inventário de hardware: códigos AAA-999, PIN e vínculo com o provedor. */
      devices: (params: { search?: string; linked?: 'yes' | 'no'; activation_state?: string; order_public_id?: string } = {}) =>
        request<{ data: AdminDevice[] }>('GET', '/admin/devices', undefined, { query: params }),
      generateDevices: (input: { device_model_id: number; quantity: number; device_batch_id?: number; serial?: string }) =>
        request<{ data: GeneratedTag[] }>('POST', '/admin/devices/generate', input),
      getDeviceActivationCode: (publicId: string) =>
        request<{ activation_code: string; expires_at: string | null }>('GET', `/admin/devices/${publicId}/activation-code`),
      reissueDeviceActivationCode: (publicId: string) =>
        request<{ activation_code: string; expires_at: string | null }>('POST', `/admin/devices/${publicId}/activation-code/reissue`),
      updateDevice: (publicId: string, input: { name?: string; serial?: string | null; supplier_identifier?: string | null }) =>
        request<{ device: Pick<AdminDevice, 'id' | 'tag_code' | 'name' | 'serial' | 'supplier_identifier' | 'activation_state'> }>('PUT', `/admin/devices/${publicId}`, input),
      // ---- Billing ops — cortesia e postergação (SPEC-BILLING §12) ------------
      /** Dias grátis sem custo: courtesy_until libera o serviço sem mexer na cobrança. */
      grantCourtesy: (accountPublicId: string, input: { days: number; reason: string }) =>
        request<{ data: { courtesy_until: string; billing: BillingSummary } }>(
          'POST', `/admin/accounts/${accountPublicId}/courtesy`, input),
      /** Empurra o vencimento do ciclo corrente — local e no provedor; auditado. */
      postponeSubscriptionDue: (subscriptionPublicId: string, input: { date: string; reason: string }) =>
        request<{ data: { subscription_id: string; state: string; current_period_ends_at: string } }>(
          'POST', `/admin/subscriptions/${subscriptionPublicId}/postpone-due`, input),

      // ---- Termos de adesão — CRUD versionado (SPEC-TERMOS §5) ----------------
      termsList: () =>
        request<{ data: AdminTerm[] }>('GET', '/admin/terms'),
      termDetail: (kind: TermKind) =>
        request<{ data: AdminTermDetail }>('GET', `/admin/terms/${kind}`),
      createTermVersion: (kind: TermKind, input: { title: string; body_md: string }) =>
        request<{ data: AdminTermVersion }>('POST', `/admin/terms/${kind}/versions`, input),
      updateTermVersion: (id: number, input: { title?: string; body_md?: string }) =>
        request<{ data: AdminTermVersion }>('PUT', `/admin/term-versions/${id}`, input),
      publishTermVersion: (id: number) =>
        request<{ data: AdminTermVersion }>('POST', `/admin/term-versions/${id}/publish`),
      archiveTermVersion: (id: number) =>
        request<{ data: AdminTermVersion }>('POST', `/admin/term-versions/${id}/archive`),
      termAcceptances: (kind: TermKind) =>
        request<{ data: AdminTermAcceptance[] }>('GET', `/admin/terms/${kind}/acceptances`),
      /** Auditoria transversal de aceites — todos os kinds, paginada. */
      termAcceptanceAudit: (params: { q?: string; kind?: TermKind; source?: string; from?: string; to?: string; page?: number } = {}) =>
        request<Paginated<AdminTermAcceptanceEntry>>('GET', '/admin/term-acceptances', undefined, { query: params }),

      /** Funil de atendimento: leads do site (contato + proposta comercial). */
      leads: (params: { status?: string; q?: string } = {}) =>
        request<{ data: AdminLead[]; counts: Record<string, number> }>('GET', '/admin/leads', undefined, { query: params }),
      updateLead: (id: number, input: { status?: string; note?: string | null; internal_notes?: string | null }) =>
        request<{ data: AdminLead }>('PATCH', `/admin/leads/${id}`, input),
      /** Blocklist: bloqueia mensagem (leads) e registro por e-mail/telefone/domínio. */
      blocklist: (params: { type?: string; q?: string } = {}) =>
        request<{ data: AdminBlocklistEntry[] }>('GET', '/admin/blocklist', undefined, { query: params }),
      createBlocklistEntry: (input: { type: string; value: string; reason?: string | null }) =>
        request<{ data: AdminBlocklistEntry }>('POST', '/admin/blocklist', input),
      deleteBlocklistEntry: (id: number) =>
        request<{ data: { removed: boolean } }>('DELETE', `/admin/blocklist/${id}`),
      /** Tags da fonte de telemetria (TagIT) para escolher no vínculo. */
      telemetryTags: (provider: string, params: { status?: string; search?: string } = {}) =>
        request<{ total: number; tags: TelemetryTag[] }>('GET', `/admin/integrations/${provider}/telemetry-tags`, undefined, { query: params }),

      // ---- Vendedores e comissões (SPEC-VENDEDORES) -------------------------
      sellers: (q = '') =>
        request<{ data: AdminSeller[] }>('GET', '/admin/sellers', undefined, { query: { q } }),
      seller: (id: number) =>
        request<{ data: AdminSeller }>('GET', `/admin/sellers/${id}`),
      createSeller: (input: SellerInput) =>
        request<{ data: AdminSeller }>('POST', '/admin/sellers', input),
      updateSeller: (id: number, input: Partial<SellerInput>) =>
        request<{ data: AdminSeller }>('PUT', `/admin/sellers/${id}`, input),
      /** Grade geral: quinzenas abertas por vendedor + últimos fechamentos. */
      commissions: () =>
        request<{ open: CommissionOpenPeriod[]; payouts: CommissionPayout[] }>('GET', '/admin/commissions'),
      /** Lançamentos e quinzenas pagas de um vendedor (aba do detalhe). */
      sellerCommissions: (id: number) =>
        request<{ entries: CommissionEntry[]; payouts: CommissionPayout[] }>('GET', `/admin/sellers/${id}/commissions`),
      /** Fecha a quinzena do vendedor — marca lançamentos accrued como pagos. */
      payCommissionPeriod: (input: { seller_id: number; period_start: string; note?: string }) =>
        request<{ payout: CommissionPayout }>('POST', '/admin/commissions/pay', input),
      /** Reabre uma quinzena já paga — lançamentos voltam a accrued. */
      reopenCommissionPayout: (payoutId: number) =>
        request<{ payout: CommissionPayout }>('POST', `/admin/commission-payouts/${payoutId}/reopen`),
    },

    // ---- Portal do vendedor (SPEC-VENDEDORES) ---------------------------------
    seller: {
      me: () => request<{ data: SellerMe }>('GET', '/seller/me'),
      proposals: () => request<{ data: SellerProposal[] }>('GET', '/seller/proposals'),
      createProposal: (input: {
        customer_name?: string
        customer_email?: string
        customer_phone?: string
        items: { sku: string; quantity: number }[]
        note?: string
        expires_at?: string
      }) => request<{ data: SellerProposal }>('POST', '/seller/proposals', input),
      cancelProposal: (id: number) =>
        request<{ data: SellerProposal }>('POST', `/seller/proposals/${id}/cancel`),
      commissions: () =>
        request<{
          periods: {
            period_start: string
            accrued_cents: number
            paid_cents: number
            entries: (Omit<CommissionEntry, 'period' | 'payment_public_id' | 'created_at'> & { paid_at: string | null })[]
          }[]
          payouts: { period_start: string; period_end: string; total_cents: number; paid_at: string | null; note: string | null }[]
          totals: { accrued_cents: number; paid_cents: number }
        }>('GET', '/seller/commissions'),
      clients: () => request<{ data: SellerClient[] }>('GET', '/seller/clients'),
    },
  }
}

export type LocziApi = ReturnType<typeof createApi>

/**
 * Lê um link público de compartilhamento — fora do cliente autenticado:
 * a página /s/:token abre para qualquer pessoa, sem sessão nem conta.
 */
export async function fetchSharedLink(baseUrl: string, token: string): Promise<SharedLinkView> {
  const res = await fetch(`${baseUrl}/api/v1/share/${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw { message: 'Link expirado ou revogado.', status: res.status } satisfies ApiError
  return (await res.json()).data
}

/**
 * Resolve uma proposta de vendedor pelo token público (?p=) — prefill do
 * checkout no site. Sem sessão: qualquer pessoa com o link abre.
 */
export async function fetchPublicProposal(baseUrl: string, token: string): Promise<PublicProposal> {
  const res = await fetch(`${baseUrl}/api/v1/proposals/${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw { message: 'Proposta expirada ou inválida.', status: res.status } satisfies ApiError
  return (await res.json()).data
}
